import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/config';
import FeedbackButton from '../components/FeedbackButton';
import { checkAndRestoreActivityCache } from '../lib/claudeApi';
import { track } from '../lib/analytics';



const TRAVELER_PROFILES = {
  'The Pioneer': 'You consider a destination ruined the moment it appears on a top ten list. You want the place before the crowds find it. Discovery is not a preference, it is a requirement.',
  'The Adventurer': 'You want to come home having seen something most people have never googled. Not just visited, but genuinely encountered. You use iconic destinations as orientation points, not destinations.',
  'The Explorer': 'One anchor to orient yourself, everything else is discovery. You find the version of a place most visitors never reach because they did not know to look.',
  'The Wanderluster': 'The destination almost does not matter. What matters is that it is somewhere new. You travel because you love to travel, full stop, and every new place is a good place.',
  'The Curious Traveler': 'Half familiar, half discovery. You come home with stories and recommendations nobody else at the dinner table has heard. You balance comfort with genuine curiosity.',
  'The Selective Explorer': 'You appreciate the classics but always find that one place nobody else has been to. You do the research, go off script at least once, and it is always the best part of the trip.',
  'The Classics': 'You know what you love and you love it for good reason. The iconic destinations deliver and you are here for every bit of it. You do the classics properly, which most people never do.',
};

const ACTIVITY_PROFILES = {
  'The Foodie': 'You experience every destination through what it grows, cooks, and serves. Markets at dawn, hole-in-the-wall restaurants locals actually eat at, dishes you have never heard of that become the story you tell when you get home.',
  'The Connoisseur': 'You travel partly for what a place produces. Vineyard visits, craft brewery tours, whisky distilleries, local spirits. Understanding what is made here and why is as important as where you eat or what you see.',
  'The Curator': 'You move through a destination like someone who has done the research and knows exactly what deserves your time. Museums, historical sites, architecture, art. You seek the best version of every cultural experience.',
  'The Wanderer': 'Water, mountains, forests, open landscapes. You feel most alive when nature is the backdrop. Whether you are hiking a ridge, sitting beside a river, or swimming in something cold and clear.',
  'The Adventurer': 'You want to come home having done something. A climb, a dive, a trail that required more than you expected. The physical challenge is the point, not the obstacle.',
  'The Live Wire': 'You come alive after dark. Live music, the right bar on the right street, a city that does not start until midnight. Nightlife is not the end of the day for you, it is often the best part of it.',
  'The Drifter': 'You do not need a plan to have a perfect trip. A good place, enough time, and the freedom to follow whatever feels right. The best moments find you rather than the other way around.',
  'The Restorer': 'You travel to come back to yourself. Spas, slow mornings, quiet coastlines, a long lunch with nowhere to be after. The best trip leaves you genuinely rested.',
  'The Immersionist': 'You want to be inside a place, not observing it. Food, neighborhoods, people, culture, daily life. You go deep across all of it rather than specializing in one dimension.',
  'The Cultural Omnivore': 'History, food, art, local life. You consume a destination across every cultural dimension simultaneously. Culture is not one thing for you, it is everything.',
  'The Maximalist': 'You want everything turned up. Adventure before lunch, a great meal after, live music at night, a hike tomorrow morning. You make the most of every activity, every environment, and every day.',
};

function Welcome() {
  const navigate = useNavigate();
  const profile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
  const travelerProfile = profile.profile || '';
  const activityProfile = profile.activity_profile || '';
  const travelerDesc = (TRAVELER_PROFILES[travelerProfile] || '').replace(/\u2014/g, '-');
  const STREAK_MODIFIERS = {
      food: "Your Foodie streak means the table is never an afterthought. The best meal of the trip is always part of the plan.",
      drink: "Your Connoisseur streak means you always find out what a place produces. A vineyard visit or local distillery finds its way into every trip.",
      culture: "Your Curator streak means you give the museums and historical sites real time. Not everything needs to be spontaneous.",
      nature: "Your Wanderer streak means you always find your way to open landscape. Water, trails, or a view that required something to reach.",
      adventure: "Your Adventurer streak means at least one thing on the trip pushes you. A climb, a dive, or something that earns the meal after.",
      nightlife: "Your Live Wire streak means you know which cities come alive after dark. You always find out where the night actually goes.",
      wellness: "Your Restorer streak means you know when to slow down. A long lunch with nowhere to be after is never wasted time.",
    };

  const DIM_TO_STREAK = {
    food: 'food', drink: 'drink', culture: 'culture',
    nature: 'nature', adventure: 'adventure', nightlife: 'nightlife', wellness: 'wellness',
  };

  const primaryActivityProfile = activityProfile.includes(' with ')
    ? activityProfile.split(' with ')[0]
    : activityProfile;

  const secondaryDimension = profile.secondary_activity_dimension || null;
  const streakLine = secondaryDimension && DIM_TO_STREAK[secondaryDimension]
    ? STREAK_MODIFIERS[DIM_TO_STREAK[secondaryDimension]]
    : '';

  const activityDesc = [
    (ACTIVITY_PROFILES[primaryActivityProfile] || '').replace(/\u2014/g, '-'),
    streakLine,
  ].filter(Boolean).join(' ');

  const [seizeOpen, setSeizeOpen] = useState(false);
  const [retakeOpen, setRetakeOpen] = useState(false);
  const [retestMode, setRetestMode] = useState(null); // 'traveler' or 'activity'
  const [retestQuestions, setRetestQuestions] = useState([]);
  const [retestIndex, setRetestIndex] = useState(0);
  const [retestAnswers, setRetestAnswers] = useState({});
  const [retestSubmitting, setRetestSubmitting] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [retestDone, setRetestDone] = useState(false);
  const [retestTransitioning, setRetestTransitioning] = useState(false);
  const [destinationInput, setDestinationInput] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const destinationInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [experienceInput, setExperienceInput] = useState('');
  const [seizeMode, setSeizeMode] = useState(null); // 'activities' or 'destinations'
  const [seizeLoading, setSeizeLoading] = useState(false);
  const [childrenToggle, setChildrenToggle] = useState(false);
  const [familyPreQuizQuestions, setFamilyPreQuizQuestions] = useState([]);
  const [familyPreQuizAnswers, setFamilyPreQuizAnswers] = useState({});
  const [familyPreQuizIndex, setFamilyPreQuizIndex] = useState(0);


  // Load Google Maps and initialize autocomplete when seizeMode is 'activities'
  useEffect(() => {
    if (seizeMode !== 'activities') return;
    
    const initAutocomplete = () => {
      if (!destinationInputRef.current || !window.google) return;
      
      // Create session token for cost-efficient billing
      const sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      
      const autocomplete = new window.google.maps.places.Autocomplete(
        destinationInputRef.current,
        {
          types: ['(cities)'],
          sessionToken: sessionToken,
        }
      );
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;
        
        let city = '';
        let state = '';
        let country = '';
        
        for (const component of place.address_components) {
          if (component.types.includes('locality')) {
            city = component.long_name;
          } else if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name;
          } else if (component.types.includes('country')) {
            country = component.long_name;
          }
        }
        
        // Fallback: use the first part of formatted address as city
        if (!city && place.name) city = place.name;
        
        const lat = place.geometry?.location?.lat ? place.geometry.location.lat() : null;
        const lng = place.geometry?.location?.lng ? place.geometry.location.lng() : null;
        
        setSelectedPlace({ city, state, country, lat, lng });
        setDestinationInput(place.formatted_address || `${city}, ${country}`);
      });
      
      autocompleteRef.current = autocomplete;
    };

    // Load Google Maps script if not already loaded
    if (window.google && window.google.maps) {
      initAutocomplete();
    } else {
      const existingScript = document.getElementById('google-maps-script');
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initAutocomplete;
        document.head.appendChild(script);
      } else {
        existingScript.addEventListener('load', initAutocomplete);
      }
    }
  }, [seizeMode]);

  if (typeof document !== 'undefined' && !document.getElementById('carpe-spinner-style')) {
    const style = document.createElement('style');
    style.id = 'carpe-spinner-style';
    style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }

function pickRetestQuestions(allQuestions, dimensions, count) {
    // Pick one question randomly from each dimension group
    const selected = [];
    const dimKeys = Object.keys(dimensions);
    for (const dim of dimKeys) {
      const pool = allQuestions.filter(q => q.dimension === dim);
      if (pool.length > 0) {
        const pick = pool[Math.floor(Math.random() * pool.length)];
        selected.push(pick);
      }
    }
    // Shuffle and return up to count
    for (let i = selected.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [selected[i], selected[j]] = [selected[j], selected[i]];
    }
    return selected.slice(0, count);
  }

  async function startRetest(type) {
    // Guard: retest blends new answers with an existing profile (60/40).
    // If there's no real profile yet (empty sessionStorage — cleared cache,
    // private browsing, a restore race condition, etc.), retest is
    // structurally meaningless and the backend will correctly reject it.
    // Redirect to the full quiz instead of letting the user hit that dead end.
    if (!profile || Object.keys(profile).length === 0) {
      handleRetakeQuiz('full');
      return;
    }
    setRetestAnswers({});
    setRetestIndex(0);
    setRetestDone(false);

   

    // Filter to the right bank and pick 6
    let questions = [];
    if (type === 'traveler') {
      const destDims = {
        explorer_score: true,
        explorer_aspirational: true,
        environment_forward: true,
        environment_backward: true,
        pace: true,
        activity_level: true,
      };
      // Use DESTINATION_MICRO_QUESTIONS from Loading.jsx — fetch them inline
      const microRes = await fetch(`${API_BASE}/quiz/micro-questions?type=destination`);
      if (microRes.ok) {
        const microData = await microRes.json();
        questions = pickRetestQuestions(microData.questions, destDims, 6);
      }
    } else {
      const actDims = {
        food: true, drink: true, culture: true,
        nature: true, adventure: true, nightlife: true, wellness: true,
      };
      const microRes = await fetch(`${API_BASE}/quiz/micro-questions?type=activity`);
      if (microRes.ok) {
        const microData = await microRes.json();
        questions = pickRetestQuestions(microData.questions, actDims, 6);
      }
    }

    if (questions.length > 0) {
      setRetestQuestions(questions);
      setRetestMode(type);
    } else {
      // Fallback to full quiz if no questions loaded
      handleRetakeQuiz('full');
    }
    setRetakeOpen(false);
  }

  function handleRetestAnswer(qid, answer) {
    setRetestAnswers(prev => ({ ...prev, [qid]: answer }));
  }

  function handleRetestNext() {
    if (retestIndex < retestQuestions.length - 1) {
      setRetestTransitioning(true);
      setTimeout(() => {
        setRetestIndex(prev => prev + 1);
        setRetestTransitioning(false);
      }, 250);
    } else {
      handleRetestSubmit();
    }
  }

  async function handleRetestSubmit() {
    setRetestSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/quiz/retest-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          retest_type: retestMode,
          micro_answers: retestAnswers,
          existing_profile: profile,
        }),
      });
      if (!res.ok) {
        console.error('Retest scoring failed:', res.status, await res.text());
        setRetestSubmitting(false);
        alert('Something went wrong updating your profile. Please try again.');
        return;
      }

      const updatedProfile = await res.json();

      // Save to sessionStorage
      sessionStorage.setItem('carpeWonderProfile', JSON.stringify(updatedProfile));

      // Save to Supabase
      const userId = sessionStorage.getItem('carpeUserId');
      const userEmail = sessionStorage.getItem('carpeUserEmail');
      if (userId) {
        const saveRes = await fetch(`${API_BASE}/auth/profile/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            email: userEmail,
            profile: updatedProfile,
          }),
        }).catch(err => {
          console.error('Retest profile save failed:', err);
          return null;
        });
        if (saveRes && !saveRes.ok) {
          console.error('Retest profile save to Supabase failed:', saveRes.status, await saveRes.text());
        }
      }

      setRetestDone(true);
      setRetestSubmitting(false);

      // Trigger background destination refresh then reload Welcome
      sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
        type: 'destinations',
        endpoint: '/recommendations/destinations',
        payload: updatedProfile,
        destination: '/results',
        loadingMessage: 'Refreshing your destinations...',
        startPassive: true,
      }));
      navigate('/loading');

    } catch {
      setRetestSubmitting(false);
    }
  }

  function handleSeeDestinations() {
    const teasers = sessionStorage.getItem('carpeEnrichTeasers');
    const hasEnrichment = teasers && Object.keys(JSON.parse(teasers)).length > 0;

    if (hasEnrichment) {
      // Everything cached — go directly to Results, no loading screen
      navigate('/results');
      return;
    }

    // Missing enrichment — fire it in background, go to Results anyway
    // Results.jsx will poll sessionStorage and show teasers as they arrive
    sessionStorage.removeItem('carpeEnrichTeasers');
    sessionStorage.removeItem('carpeEnrichFacts');
    const savedDests = JSON.parse(sessionStorage.getItem('carpeSavedDestinations') || '[]');
    const bgProfile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
    if (savedDests.length > 0) {
      savedDests.forEach(dest => {
        fetch(`${API_BASE}/recommendations/destinations/enrich-teasers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile: bgProfile, destinations: [dest], user_id: sessionStorage.getItem('carpeUserId') }),
        })
          .then(res => res.json())
          .then(enrichData => {
            const enriched = enrichData.enriched || [];
            if (enriched.length > 0) {
              const current = JSON.parse(sessionStorage.getItem('carpeEnrichTeasers') || '{}');
              current[enriched[0].name] = enriched[0].teaser_activities || [];
              sessionStorage.setItem('carpeEnrichTeasers', JSON.stringify(current));
            }
          })
          .catch(() => {});
        fetch('/api/recommendations/destinations/enrich-facts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile: bgProfile, destinations: [dest], user_id: sessionStorage.getItem('carpeUserId') }),
        })
          .then(res => res.json())
          .then(enrichData => {
            const enriched = enrichData.enriched || [];
            if (enriched.length > 0) {
              const current = JSON.parse(sessionStorage.getItem('carpeEnrichFacts') || '{}');
              current[enriched[0].name] = {
                profile_match_facts: enriched[0].profile_match_facts || [],
                profile_surprise_facts: enriched[0].profile_surprise_facts || [],
              };
              sessionStorage.setItem('carpeEnrichFacts', JSON.stringify(current));
            }
          })
          .catch(() => {});
      });
    }
    // Go to Results immediately — enrichment fires in background
    navigate('/results');
  }

  function handleRetakeQuiz(type) {
    sessionStorage.removeItem('carpeWasReturning');
    sessionStorage.removeItem('carpeSavedDestinations');
    sessionStorage.removeItem('carpeReturningUser');
    navigate('/quiz');
  }

  function handleSignOut() {
    sessionStorage.clear();
    localStorage.removeItem('carpeUserId');
    localStorage.removeItem('carpeUserEmail');
    localStorage.removeItem('carpeAccessToken');
    localStorage.removeItem('carpeRefreshToken');
    localStorage.removeItem('carpeKeepSignedIn');
    navigate('/');
  }

  async function activateChildrenToggle() {
    const currentProfile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
    const hasExistingFamilyAnswers = currentProfile.family_micro_answers &&
      Object.keys(currentProfile.family_micro_answers).length > 0;
    // Always set toggle on — questions show inline if needed
    setChildrenToggle(true);
    if (hasExistingFamilyAnswers) return;
    // Load questions into state if not already cached
    if (familyPreQuizQuestions.length > 0) {
      setFamilyPreQuizAnswers({});
      setFamilyPreQuizIndex(0);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/quiz/family-pre-quiz`);
      const data = await res.json();
      setFamilyPreQuizQuestions(data.questions);
      setFamilyPreQuizAnswers({});
      setFamilyPreQuizIndex(0);
    } catch {
      // Questions failed to load — toggle stays on, no quiz shown
    }
  }

  function familyPreQuizSelected(qid, letter, isMulti, maxSel) {
    if (isMulti) {
      const current = familyPreQuizAnswers[qid] || [];
      if (current.includes(letter)) {
        setFamilyPreQuizAnswers(prev => ({ ...prev, [qid]: current.filter(l => l !== letter) }));
      } else if (current.length < maxSel) {
        setFamilyPreQuizAnswers(prev => ({ ...prev, [qid]: [...current, letter] }));
      }
    } else {
      setFamilyPreQuizAnswers(prev => ({ ...prev, [qid]: letter }));
    }
  }

  function familyPreQuizHasAnswer() {
    const q = familyPreQuizQuestions[familyPreQuizIndex];
    if (!q) return false;
    const ans = familyPreQuizAnswers[q.id];
    if (!ans) return false;
    if (Array.isArray(ans)) return ans.length > 0;
    return true;
  }

  
 async function handleSeizeActivities(profileOverride = null) {
    if (!destinationInput.trim()) return;
    setSeizeLoading(true);
    
    // Use structured place data if available, otherwise fall back to text parsing
    let destination, country, stateProvince;
    if (selectedPlace && selectedPlace.city) {
      destination = selectedPlace.city;
      country = selectedPlace.country;
      // Don't use state if it's the same as the city (e.g. Tokyo, Tokyo)
      stateProvince = (selectedPlace.state && selectedPlace.state !== selectedPlace.city) 
        ? selectedPlace.state 
        : null;
    } else {
      const parts = destinationInput.split(',').map(p => p.trim());
      destination = parts[0] || destinationInput;
      country = parts[1] || '';
      stateProvince = null;
    }

    const destObj = {
      name: destination,
      country: country,
      state_province: stateProvince,
      lat: selectedPlace?.lat || null,
      lng: selectedPlace?.lng || null,
      event_date: eventDate || null,
      teaser_activities: [],
    };
    sessionStorage.setItem('carpeDestination', JSON.stringify(destObj));
    track('Seize Activities', { destination, has_date: !!eventDate, children_toggled: !!childrenToggle });
    const activeProfile = { ...(profileOverride || profile) };
    if (childrenToggle) activeProfile.children_flag = true;
    sessionStorage.setItem('carpeWonderProfile', JSON.stringify(activeProfile));
    sessionStorage.setItem('carpeActivitiesReferrer', '/welcome');

    // Check for cached activities first
    const userId = sessionStorage.getItem('carpeUserId');
    if (userId) {
      const restored = await checkAndRestoreActivityCache(userId, destination, eventDate || null);
      if (restored) {
        setSeizeLoading(false);
        navigate('/activities');
        return;
      }
    }

    const prof = activeProfile;
    sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
      type: 'activities',
      endpoint: '/recommendations/activities',
      payload: {
        destination: destination,
        country: country,
        state_province: stateProvince,
        lat: selectedPlace?.lat || null,
        lng: selectedPlace?.lng || null,
        event_date: eventDate || null,
        profile: {
          profile: prof.profile,
          pace: prof.pace,
          activity_level: prof.activity_level,
          best_for_tags: prof.best_for_tags,
          social_vibe: prof.social_vibe,
          explorer_score: prof.explorer_score,
          activity_profile: prof.activity_profile,
          activity_dimensions: prof.activity_dimensions,
          activity_profile_description: prof.activity_profile_description,
        },
        micro_answers: JSON.parse(sessionStorage.getItem('carpeMicroAnswers') || '{}'),
        teaser_activities: [],
      },
      destination: '/activities',
      loadingMessage: 'Finding your activities in ' + destination + '...',
    }));

    navigate('/loading');
  }

  async function handleSeizeDestinations(profileOverride = null) {
    if (!experienceInput.trim()) return;
    track('Experience Search', { experience: experienceInput, children_toggled: !!childrenToggle });
    setSeizeLoading(true);
    const baseProfile = profileOverride || profile;
    const activeProfile = {
      profile: baseProfile.profile,
      pace: baseProfile.pace,
      activity_level: baseProfile.activity_level,
      best_for_tags: baseProfile.best_for_tags,
      social_vibe: baseProfile.social_vibe,
      explorer_score: baseProfile.explorer_score,
      activity_profile: baseProfile.activity_profile,
      activity_dimensions: baseProfile.activity_dimensions,
      activity_profile_description: baseProfile.activity_profile_description,
      children_flag: childrenToggle ? true : (baseProfile.children_flag || false),
      children_ages: baseProfile.children_ages || [],
      family_micro_answers: baseProfile.family_micro_answers || {},
    };
    sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
      type: 'destinations-by-experience',
      endpoint: '/recommendations/destinations-by-experience',
      payload: {
        experience: experienceInput,
        profile: activeProfile,
      },
      destination: '/results',
      loadingMessage: 'Finding destinations for your experience...',
    }));
    sessionStorage.removeItem('carpeWasReturning');
    navigate('/loading');
  }
  
  
  return (
    <div style={styles.page}>
      <FeedbackButton />
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logo}>Carpe Wonder™</div>
          <button style={styles.signOutBtn} onClick={handleSignOut}>
            Sign out
          </button>
        </div>

        {/* Traveler Profile Card */}
        {travelerProfile && (
        <div style={styles.profileCard}>
            <div style={styles.profileLabel}>Your traveler profile</div>
            <div style={{...styles.profileName, color: '#b496dc'}}>{travelerProfile}</div>
            <div style={styles.profileDesc}>{travelerDesc}</div>
        </div>
        )}

        {/* Activity Profile Card */}
        {activityProfile && (
        <div style={{...styles.profileCard, ...styles.activityCard}}>
            <div style={styles.profileLabel}>Your activity profile</div>
            <div style={{...styles.profileName, color: '#48b4a0'}}>{activityProfile}</div>
            <div style={styles.profileDesc}>{activityDesc}</div>
        </div>
        )}

        {/* See My Destinations */}
        <button style={styles.primaryBtn} onClick={handleSeeDestinations}>
          See my destinations →
        </button>
        {/* Destination History */}
        <button style={styles.secondaryBtn} onClick={() => navigate('/history')}>
          My destination history →
        </button>
        {/* Saved Activities */}
        <button style={styles.secondaryBtn} onClick={() => navigate('/saved-activities')}>
          My saved activities →
        </button>

        {/* Seize What I Want */}
        <div style={styles.expandCard}>
          <button
            style={styles.expandHeader}
            onClick={() => {
              setSeizeOpen(prev => !prev);
              setRetakeOpen(false);
              setSeizeMode(null);
            }}
          >
            <span>Seize what I want</span>
            <span>{seizeOpen ? '↑' : '↓'}</span>
          </button>

          {seizeOpen && (
            <div style={styles.expandBody}>
              {!seizeMode && (
                <>
                  <button
                    style={styles.expandOption}
                    onClick={() => setSeizeMode('activities')}
                  >
                    <div style={styles.expandOptionTitle}>I know where I'm going</div>
                    <div style={styles.expandOptionDesc}>Enter a destination and get activities matched to your activity profile</div>
                  </button>
                  <button
                    style={styles.expandOption}
                    onClick={() => setSeizeMode('destinations')}
                  >
                    <div style={styles.expandOptionTitle}>I know what I want to do</div>
                    <div style={styles.expandOptionDesc}>Enter an experience and get destinations matched to your traveler profile</div>
                  </button>
                </>
              )}

              {seizeMode === 'activities' && (
                <div style={styles.inputWrap}>
                  <button style={styles.backLink} onClick={() => setSeizeMode(null)}>← Back</button>
                  <div style={styles.inputLabel}>Where are you headed?</div>
                  <input
                    ref={destinationInputRef}
                    style={styles.input}
                    placeholder="Start typing a city..."
                    value={destinationInput}
                    onChange={e => {
                      setDestinationInput(e.target.value);
                      setSelectedPlace(null); // clear structured data on manual edit
                    }}
                    onKeyDown={e => e.key === 'Enter' && !seizeLoading && handleSeizeActivities()}
                    disabled={seizeLoading}
                  />
                  <input
                    type="date"
                    style={{ ...styles.input, marginTop: '8px' }}
                    value={eventDate}
                    onChange={e => setEventDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={seizeLoading}
                  />
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '-4px', marginBottom: '4px' }}>
                    Optional — add your travel date to surface live events happening while you're there
                  </div>
                  <button
                    style={styles.geoBtn}
                    onClick={() => {
                      if (!navigator.geolocation) return;
                      setDestinationInput('Detecting your location...');
                      navigator.geolocation.getCurrentPosition(
                        async (pos) => {
                          const { latitude, longitude } = pos.coords;
                          try {
                            const res = await fetch(
                              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}`
                            );
                            const data = await res.json();
                            const components = data.results[0]?.address_components || [];
                            let city = '';
                            let state = '';
                            let country = '';
                            for (const c of components) {
                              if (c.types.includes('locality')) city = c.long_name;
                              else if (c.types.includes('administrative_area_level_1')) state = c.long_name;
                              else if (c.types.includes('country')) country = c.long_name;
                            }
                            if (!city) city = data.results[0]?.address_components?.[0]?.long_name || '';
                            setSelectedPlace({ city, state, country });
                            setDestinationInput(`${city}${state ? ', ' + state : ''}, ${country}`);
                          } catch {
                            setDestinationInput('');
                          }
                        },
                        () => setDestinationInput('')
                      );
                    }}
                    disabled={seizeLoading}
                  >
                    📍 Use my current location
                  </button>
                  {seizeLoading && (
                    <div style={styles.loadingState}>
                      <div style={styles.spinner} />
                      <div style={styles.loadingText}>Finding your activities...</div>
                    </div>
                  )}
                  {!seizeLoading && (
                    <>
                      <button
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: 'transparent', border: 'none',
                          cursor: 'pointer', padding: '8px 0', margin: '4px 0',
                        }}
                        onClick={() => {
                          if (!childrenToggle) activateChildrenToggle();
                          else {
                            setChildrenToggle(false);
                            setFamilyPreQuizAnswers({});
                            setFamilyPreQuizIndex(0);
                          }
                        }}
                      >
                        <span style={{
                          width: '18px', height: '18px', borderRadius: '3px',
                          border: `2px solid ${childrenToggle ? 'var(--gold)' : 'rgba(255,255,255,0.3)'}`,
                          background: childrenToggle ? 'var(--gold)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, transition: 'all 0.15s ease',
                        }}>
                          {childrenToggle && (
                            <span style={{ color: '#0d0d1f', fontSize: '11px', fontWeight: '700', lineHeight: 1 }}>✓</span>
                          )}
                        </span>
                        <span style={{ fontSize: '14px', color: childrenToggle ? 'var(--gold)' : 'rgba(255,255,255,0.7)' }}>
                          Traveling with children
                        </span>
                      </button>
                      {childrenToggle && familyPreQuizQuestions.length > 0 && (() => {
                        const currentProfile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
                        const needsQuiz = !currentProfile.family_micro_answers ||
                          Object.keys(currentProfile.family_micro_answers).length === 0;
                        if (!needsQuiz) return null;
                        const fq = familyPreQuizQuestions[familyPreQuizIndex];
                        if (!fq) return null;
                        const fIsMulti = fq.multi_select;
                        const fSelected = (letter) => {
                          const ans = familyPreQuizAnswers[fq.id];
                          if (!ans) return false;
                          if (Array.isArray(ans)) return ans.includes(letter);
                          return ans === letter;
                        };
                        return (
                          <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                            <div style={{ fontSize: '12px', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                              Question {familyPreQuizIndex + 1} of {familyPreQuizQuestions.length}
                            </div>
                            <div style={{ fontSize: '15px', color: '#fff', fontWeight: '500', marginBottom: '4px', lineHeight: '1.4' }}>
                              {fq.text}
                            </div>
                            {fq.note && (
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>
                                {fq.note}
                              </div>
                            )}
                            {fIsMulti && (
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>
                                Select up to {fq.max_selections}
                              </div>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                              {Object.entries(fq.options).map(([letter, text]) => {
                                const selected = fSelected(letter);
                                return (
                                  <button
                                    key={letter}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '10px',
                                      padding: '10px 12px', borderRadius: '10px', border: '1px solid',
                                      borderColor: selected ? 'var(--gold)' : 'rgba(255,255,255,0.12)',
                                      background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
                                      color: selected ? 'var(--gold)' : 'rgba(255,255,255,0.7)',
                                      cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                                    }}
                                    onClick={() => familyPreQuizSelected(fq.id, letter, fIsMulti, fq.max_selections || 4)}
                                  >
                                    <span style={{ fontWeight: '600', minWidth: '16px' }}>{letter.toUpperCase()}</span>
                                    <span>{text}</span>
                                  </button>
                                );
                              })}
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                              {familyPreQuizIndex > 0 && (
                                <button
                                  style={styles.retestCancelBtn}
                                  onClick={() => setFamilyPreQuizIndex(prev => prev - 1)}
                                >
                                  ← Back
                                </button>
                              )}
                              <button
                                style={{
                                  ...styles.retestNextBtn,
                                  opacity: familyPreQuizHasAnswer() ? 1 : 0.35,
                                  cursor: familyPreQuizHasAnswer() ? 'pointer' : 'not-allowed',
                                }}
                                disabled={!familyPreQuizHasAnswer()}
                                onClick={() => {
                                  if (familyPreQuizIndex < familyPreQuizQuestions.length - 1) {
                                    setFamilyPreQuizIndex(prev => prev + 1);
                                  } else {
                                    const prof = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
                                    prof.family_micro_answers = familyPreQuizAnswers;
                                    prof.children_flag = true;
                                    sessionStorage.setItem('carpeWonderProfile', JSON.stringify(prof));
                                    const userId = sessionStorage.getItem('carpeUserId');
                                    const userEmail = sessionStorage.getItem('carpeUserEmail');
                                    if (userId) {
                                      fetch(`${API_BASE}/auth/profile/save`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          user_id: userId,
                                          email: userEmail,
                                          profile: prof,
                                        }),
                                      }).catch(() => {});
                                    }
                                    handleSeizeActivities(prof);
                                  }
                                }}
                              >
                                {familyPreQuizIndex === familyPreQuizQuestions.length - 1 ? 'Seize my activities →' : 'Next →'}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      {(() => {
                        const currentProfile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
                        const quizComplete = !childrenToggle ||
                          (currentProfile.family_micro_answers &&
                          Object.keys(currentProfile.family_micro_answers).length > 0);
                        if (!quizComplete) return null;
                        return (
                          <button
                            style={{
                              ...styles.seizeBtn,
                              opacity: destinationInput.trim() ? 1 : 0.4,
                              cursor: destinationInput.trim() ? 'pointer' : 'not-allowed',
                            }}
                            onClick={() => handleSeizeActivities()}
                            disabled={!destinationInput.trim()}
                          >
                            Seize my activities →
                          </button>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}

              {seizeMode === 'destinations' && (
                <div style={styles.inputWrap}>
                  <button style={styles.backLink} onClick={() => setSeizeMode(null)}>← Back</button>
                  <div style={styles.inputLabel}>What experience are you after?</div>
                  <input
                    style={styles.input}
                    placeholder="A cuisine, a landscape, a festival..."
                    value={experienceInput}
                    onChange={e => setExperienceInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !seizeLoading && handleSeizeDestinations()}
                    disabled={seizeLoading}
                  />
                  {seizeLoading && (
                    <div style={styles.loadingState}>
                      <div style={styles.spinner} />
                      <div style={styles.loadingText}>Finding your destinations...</div>
                    </div>
                  )}
                  {!seizeLoading && (
                    <>
                      <button
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: 'transparent', border: 'none',
                          cursor: 'pointer', padding: '8px 0', margin: '4px 0',
                        }}
                        onClick={() => {
                          if (!childrenToggle) activateChildrenToggle();
                          else {
                            setChildrenToggle(false);
                            setFamilyPreQuizAnswers({});
                            setFamilyPreQuizIndex(0);
                          }
                        }}
                      >
                        <span style={{
                          width: '18px', height: '18px', borderRadius: '3px',
                          border: `2px solid ${childrenToggle ? 'var(--gold)' : 'rgba(255,255,255,0.3)'}`,
                          background: childrenToggle ? 'var(--gold)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, transition: 'all 0.15s ease',
                        }}>
                          {childrenToggle && (
                            <span style={{ color: '#0d0d1f', fontSize: '11px', fontWeight: '700', lineHeight: 1 }}>✓</span>
                          )}
                        </span>
                        <span style={{ fontSize: '14px', color: childrenToggle ? 'var(--gold)' : 'rgba(255,255,255,0.7)' }}>
                          Traveling with children
                        </span>
                      </button>
                      {childrenToggle && familyPreQuizQuestions.length > 0 && (() => {
                        const currentProfile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
                        const needsQuiz = !currentProfile.family_micro_answers ||
                          Object.keys(currentProfile.family_micro_answers).length === 0;
                        if (!needsQuiz) return null;
                        const fq = familyPreQuizQuestions[familyPreQuizIndex];
                        if (!fq) return null;
                        const fIsMulti = fq.multi_select;
                        const fSelected = (letter) => {
                          const ans = familyPreQuizAnswers[fq.id];
                          if (!ans) return false;
                          if (Array.isArray(ans)) return ans.includes(letter);
                          return ans === letter;
                        };
                        return (
                          <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                            <div style={{ fontSize: '12px', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                              Question {familyPreQuizIndex + 1} of {familyPreQuizQuestions.length}
                            </div>
                            <div style={{ fontSize: '15px', color: '#fff', fontWeight: '500', marginBottom: '4px', lineHeight: '1.4' }}>
                              {fq.text}
                            </div>
                            {fq.note && (
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>
                                {fq.note}
                              </div>
                            )}
                            {fIsMulti && (
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>
                                Select up to {fq.max_selections}
                              </div>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                              {Object.entries(fq.options).map(([letter, text]) => {
                                const selected = fSelected(letter);
                                return (
                                  <button
                                    key={letter}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '10px',
                                      padding: '10px 12px', borderRadius: '10px', border: '1px solid',
                                      borderColor: selected ? 'var(--gold)' : 'rgba(255,255,255,0.12)',
                                      background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
                                      color: selected ? 'var(--gold)' : 'rgba(255,255,255,0.7)',
                                      cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                                    }}
                                    onClick={() => familyPreQuizSelected(fq.id, letter, fIsMulti, fq.max_selections || 4)}
                                  >
                                    <span style={{ fontWeight: '600', minWidth: '16px' }}>{letter.toUpperCase()}</span>
                                    <span>{text}</span>
                                  </button>
                                );
                              })}
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                              {familyPreQuizIndex > 0 && (
                                <button
                                  style={styles.retestCancelBtn}
                                  onClick={() => setFamilyPreQuizIndex(prev => prev - 1)}
                                >
                                  ← Back
                                </button>
                              )}
                              <button
                                style={{
                                  ...styles.retestNextBtn,
                                  opacity: familyPreQuizHasAnswer() ? 1 : 0.35,
                                  cursor: familyPreQuizHasAnswer() ? 'pointer' : 'not-allowed',
                                }}
                                disabled={!familyPreQuizHasAnswer()}
                                onClick={() => {
                                  if (familyPreQuizIndex < familyPreQuizQuestions.length - 1) {
                                    setFamilyPreQuizIndex(prev => prev + 1);
                                  } else {
                                    const prof = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
                                    prof.family_micro_answers = familyPreQuizAnswers;
                                    prof.children_flag = true;
                                    sessionStorage.setItem('carpeWonderProfile', JSON.stringify(prof));
                                    const userId = sessionStorage.getItem('carpeUserId');
                                    const userEmail = sessionStorage.getItem('carpeUserEmail');
                                    if (userId) {
                                      fetch(`${API_BASE}/auth/profile/save`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          user_id: userId,
                                          email: userEmail,
                                          profile: prof,
                                        }),
                                      }).catch(() => {});
                                    }
                                    handleSeizeDestinations(prof);
                                  }
                                }}
                              >
                                {familyPreQuizIndex === familyPreQuizQuestions.length - 1 ? 'Seize my destinations →' : 'Next →'}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      {(() => {
                        const currentProfile = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
                        const quizComplete = !childrenToggle ||
                          (currentProfile.family_micro_answers &&
                          Object.keys(currentProfile.family_micro_answers).length > 0);
                        if (!quizComplete) return null;
                        return (
                          <button
                            style={{
                              ...styles.seizeBtn,
                              opacity: experienceInput.trim() ? 1 : 0.4,
                              cursor: experienceInput.trim() ? 'pointer' : 'not-allowed',
                            }}
                            onClick={() => handleSeizeDestinations()}
                            disabled={!experienceInput.trim()}
                          >
                            Seize my destinations →
                          </button>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Retake Quiz */}
        {!retestMode && (
        <div style={styles.expandCard}>
          <button
            style={styles.expandHeader}
            onClick={() => {
              setRetakeOpen(prev => !prev);
              setSeizeOpen(false);
            }}
          >
            <span>Retake quiz</span>
            <span>{retakeOpen ? '↑' : '↓'}</span>
          </button>

          {retakeOpen && (
            <div style={styles.expandBody}>
              <button
                style={styles.expandOption}
                onClick={() => handleRetakeQuiz('full')}
              >
                <div style={styles.expandOptionTitle}>Full quiz</div>
                <div style={styles.expandOptionDesc}>Rebuild both your traveler and activity profiles from scratch</div>
              </button>
              <button
                style={styles.expandOption}
                onClick={() => startRetest('traveler')}
              >
                <div style={styles.expandOptionTitle}>Traveler profile retest</div>
                <div style={styles.expandOptionDesc}>6 targeted questions to refine where you go without changing your activity profile</div>
              </button>
              <button
                style={styles.expandOption}
                onClick={() => startRetest('activity')}
              >
                <div style={styles.expandOptionTitle}>Activity profile retest</div>
                <div style={styles.expandOptionDesc}>6 targeted questions to refine what you do without changing your traveler profile</div>
              </button>
            </div>
          )}
        </div>
        )}

        {/* Retest Mini Quiz */}
        {retestMode && (
          <div style={styles.retestCard}>
            <div style={styles.retestHeader}>
              <div style={styles.retestTitle}>
                {retestMode === 'traveler' ? 'Traveler profile retest' : 'Activity profile retest'}
              </div>
              <div style={styles.retestProgress}>
                {retestIndex + 1} of {retestQuestions.length}
              </div>
            </div>

            <div style={styles.retestProgressTrack}>
              <div style={{
                ...styles.retestProgressFill,
                width: `${((retestIndex + 1) / Math.max(retestQuestions.length, 1)) * 100}%`
              }} />
            </div>

            {retestQuestions.length > 0 && (
              <div style={{
                opacity: retestTransitioning ? 0 : 1,
                transition: 'opacity 0.25s ease',
              }}>
                <div style={styles.retestQuestion}>
                  {retestQuestions[retestIndex]?.text}
                </div>
                <div style={styles.retestOptions}>
                  {Object.entries(retestQuestions[retestIndex]?.options || {}).map(([letter, text]) => {
                    const qid = retestQuestions[retestIndex].id;
                    const selected = retestAnswers[qid] === letter;
                    return (
                      <button
                        key={letter}
                        style={{
                          ...styles.retestOption,
                          borderColor: selected ? 'var(--gold)' : 'rgba(255,255,255,0.15)',
                          background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.04)',
                          color: selected ? 'var(--gold)' : 'rgba(255,255,255,0.75)',
                        }}
                        onClick={() => handleRetestAnswer(qid, letter)}
                      >
                        <span style={styles.retestOptionLetter}>{letter.toUpperCase()}</span>
                        <span>{text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={styles.retestFooter}>
              <button
                style={styles.retestCancelBtn}
                onClick={() => setRetestMode(null)}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.retestNextBtn,
                  opacity: retestAnswers[retestQuestions[retestIndex]?.id] ? 1 : 0.35,
                  cursor: retestAnswers[retestQuestions[retestIndex]?.id] ? 'pointer' : 'not-allowed',
                }}
                onClick={handleRetestNext}
                disabled={!retestAnswers[retestQuestions[retestIndex]?.id] || retestSubmitting}
              >
                {retestSubmitting
                  ? 'Updating...'
                  : retestIndex === retestQuestions.length - 1
                  ? 'Update my profile →'
                  : 'Next →'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    padding: '0 0 48px',
  },
  container: {
    maxWidth: '480px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '28px 0 8px',
  },
  logo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  signOutBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.45)',
    fontSize: '12px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  profileCard: {
    border: '1.5px solid rgba(180,150,220,0.6)',
    borderTop: '3px solid #b496dc',
    borderRadius: '14px',
    padding: '18px 20px',
    background: 'rgba(180,150,220,0.05)',
  },
  activityCard: {
    border: '1.5px solid rgba(72,180,160,0.6)',
    borderTop: '3px solid #48b4a0',
    background: 'rgba(72,180,160,0.05)',
  },
  profileLabel: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.45)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: '6px',
  },
  profileName: {
    fontSize: '18px',
    fontWeight: '500',
    marginBottom: '8px',
  },
  profileDesc: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: '1.7',
  },
  primaryBtn: {
    width: '100%',
    background: 'var(--gold)',
    color: '#0d0d1f',
    border: 'none',
    borderRadius: '28px',
    padding: '16px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '44px',
    marginTop: '8px',
  },
  secondaryBtn: {
    width: '100%',
    background: 'transparent',
    color: 'var(--gold)',
    border: '1px solid rgba(201,168,76,0.4)',
    borderRadius: '28px',
    padding: '16px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '44px',
    marginTop: '8px',
  },
  expandCard: {
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: '14px',
    background: '#1a1a35',
    overflow: 'hidden',
  },
  expandHeader: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '500',
    minHeight: '44px',
  },
  expandBody: {
    padding: '0 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    borderTop: '0.5px solid rgba(255,255,255,0.06)',
  },
  expandOption: {
    background: 'rgba(255,255,255,0.03)',
    border: '0.5px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    padding: '14px 16px',
    cursor: 'pointer',
    textAlign: 'left',
    marginTop: '4px',
    minHeight: '44px',
  },
  expandOptionTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--gold)',
    marginBottom: '4px',
  },
  expandOptionDesc: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: '1.5',
  },
  inputWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: '8px',
  },
  inputLabel: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.75)',
    fontWeight: '500',
  },
  input: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    padding: '12px 14px',
    fontSize: '15px',
    color: '#fff',
    outline: 'none',
    width: '100%',
    minHeight: '44px',
  },
  geoBtn: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px 0',
    textAlign: 'center',
    minHeight: '44px',
  },
  seizeBtn: {
    width: '100%',
    background: 'transparent',
    border: '1px solid var(--gold)',
    borderRadius: '24px',
    color: 'var(--gold)',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    minHeight: '44px',
  },
  backLink: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '0',
    textAlign: 'left',
    minHeight: '44px',
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
  },
  spinner: {
    width: '18px',
    height: '18px',
    border: '2px solid rgba(255,255,255,0.2)',
    borderTop: '2px solid #c9a84c',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    flexShrink: 0,
  },
  loadingText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
  },
  retestCard: {
    border: '0.5px solid rgba(255,255,255,0.1)',
    borderRadius: '14px',
    background: '#1a1a35',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  retestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  retestTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--gold)',
    letterSpacing: '0.05em',
  },
  retestProgress: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.45)',
  },
  retestProgressTrack: {
    height: '3px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  retestProgressFill: {
    height: '100%',
    background: 'var(--gold)',
    borderRadius: '2px',
    transition: 'width 0.4s ease',
  },
  retestQuestion: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#fff',
    lineHeight: '1.5',
    marginBottom: '12px',
  },
  retestOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  retestOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    border: '1px solid',
    borderRadius: '10px',
    padding: '12px 14px',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    width: '100%',
    minHeight: '44px',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  retestOptionLetter: {
    fontSize: '12px',
    fontWeight: '600',
    minWidth: '18px',
    opacity: 0.7,
    marginTop: '1px',
  },
  retestFooter: {
    display: 'flex',
    gap: '10px',
    marginTop: '4px',
  },
  retestCancelBtn: {
    flex: 1,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '24px',
    color: 'rgba(255,255,255,0.5)',
    padding: '12px',
    fontSize: '14px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  retestNextBtn: {
    flex: 2,
    background: 'var(--gold)',
    border: 'none',
    borderRadius: '24px',
    color: '#0d0d1f',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '44px',
    transition: 'opacity 0.2s ease',
  },
};

export default Welcome;